/**
 * Game page composable following SOLID principles
 * Single Responsibility: Handles common game page initialization and state management
 */
import { ref, reactive, computed, onMounted, onBeforeUnmount } from '@vue/composition-api';
import { getters, mutations } from "@/components/game/store";
import { selectRandomContent, formatContent } from '../utils/gameUtils';
// import eventBus from "@/components/game/store/eventBus";

/**
 * Composable for common game page functionality
 * @param {string} topicType - The type of topic (exhibit, related, soul, etc.)
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive state and methods for game page
 */
export function useGamePage(topicType, options = {}) {
  // Reactive state
  const hasInitDone = ref(false);
  const progress = ref([]);
  const sendSearchForm = ref(false);
  
  // Banner configuration
  const bannerProps = reactive({
    imgSrc: '',
    content: '',
    banner_avatar: '',
    styles: options.bannerStyles || "top: 50px; left: 155px;"
  });
  
  // Background and icons
  const background = reactive({
    mainBg: ""
  });
  
  const icon = reactive({});
  
  // Computed properties
  const allInitDone = computed(() => hasInitDone.value);
  
  /**
   * Initialize view data from topic info
   * Single Responsibility: Only handles view initialization
   */
  const initViewData = () => {
    const topicInfo = getters.getTopicInfo(topicType);
    
    // Banner setup
    bannerProps.imgSrc = topicInfo.banner;
    bannerProps.content = selectRandomContent(topicInfo.banner_content);
    bannerProps.banner_avatar = topicInfo.banner_avatar;
    
    // Background setup
    background.mainBg = topicInfo.background;
    
    // Icon setup - merge topic icons into reactive icon object
    Object.assign(icon, topicInfo);
    
    return topicInfo;
  };
  
  /**
   * Initialize common game data
   * @param {Object} data - API response data
   */
  const initGameData = (data) => {
    progress.value = data.progress || [];
    return data;
  };
  
  /**
   * Handle loading state management
   * Open/Closed Principle: Easy to extend with additional loading logic
   */
  const handleLoadingState = () => {
    allInitDone.value ? mutations.closeLoading() : mutations.openLoading();
  };
  
  /**
   * Complete initialization
   */
  const completeInit = () => {
    hasInitDone.value = true;
  };
  
  /**
   * Generic search handler with validation
   * @param {string} queryText - Search query
   * @param {Array} items - Items to search through
   * @param {Function} searchLogic - Custom search logic
   * @param {Function} updateProgress - Progress update function
   */
  const handleSearch = async (queryText, items, searchLogic, updateProgress) => {
    if (sendSearchForm.value || !queryText || !queryText.trim()) return null;
    
    sendSearchForm.value = true;
    
    try {
      const result = await searchLogic(queryText, items, progress.value);
      
      if (result && updateProgress) {
        updateProgress(result);
      }
      
      return result;
    } finally {
      sendSearchForm.value = false;
    }
  };
  
  /**
   * Update progress for found items
   * @param {Object} newItem - New found item
   */
  const updateProgress = (newItem) => {
    if (!newItem || !newItem.id || progress.value.includes(newItem.id)) return;

    progress.value.push(newItem.id);
    return newItem;
  };

  // Lifecycle management
  onMounted(() => {
    // Watch for loading state changes
    // This replaces the watch in the original components
  });
  
  onBeforeUnmount(() => {
    // Cleanup if needed
  });
  
  return {
    // State
    hasInitDone,
    progress,
    sendSearchForm,
    bannerProps,
    background,
    icon,

    // Computed
    allInitDone,

    // Methods
    initViewData,
    initGameData,
    handleLoadingState,
    completeInit,
    handleSearch,
    updateProgress,

    // Utilities (exposed for backward compatibility)
    formatContent,
    selectRandomContent
  };
}